import React, { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import SpinnerComponent from '../../../Spinner.component';
import BaseModalComponent, { StyledBaseModal, StyledBaseModalContent } from '../../BaseModal.component';
import { useTranslation } from 'react-i18next';
import FormLayoutComponent from '../../../Form/FormLayout.component';
import SelectComponent from '../../../Input/Select.component';
import InputComponent from '../../../Input/Input.component';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';
import toTitle from '../../../../utils/toTitle';
import ButtonComponent, { StyledButton } from '../../../Button.component';
import { useAppSelector } from '../../../../hooks/useAppSelector.hook';
import { uuidv4 } from '../../../../utils/uuid';
import IconButtonComponent from '../../../IconButton.component';
import TrashIcon from '../../../Icons/Trash.icon';
import { StyledBaseIcon } from '../../../Icons/Base.icon';
import { PersonLink } from '../../../../redux/Candidate/Link/PersonLink.type';
import { getPersonLinkTypesThunk } from '../../../../redux/Candidate/Link/PersonLink.thunk';
import {
  getSelectedPersonLink,
  setSelectedPersonLinkAction,
} from '../../../../redux/Candidate/Link/PersonLink.slice';
import { useModalManager } from '../../../../hooks/ModalManager.hook';
import ExternalLinkDeleteAlertComponent from '../../../Alert/Candidate/Link/ExternalLinkDeleteAlert.component';
import { ALERT_ACTION_ENUM } from '../../../../types/action.types';
import useFormHook from '../../../../hooks/useForm.hook';
import { required } from '../../../../services/validator.service';

interface PersonLinkEditModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  info?: PersonLink;
  handleChange?: Function;
}

const StyledPersonLinkEditFooterRight = styled.div`
  display: flex;
  gap: 10px;
`;

const StyledPersonLinkEditFooterLeft = styled.div`
  ${StyledBaseIcon}, & > *:hover ${StyledBaseIcon} {
    color: ${({ theme }) => theme.colors.RED.R400};
  }
`;

const StyledPersonLinkEditFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 10px 0 0 0;
  gap: 10px;

  ${StyledButton} {
    min-width: 100px;
  }
`;

const StyledPersonLinkFormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledPersonLinkForm = styled.div``;

const StyledPersonLinkEdit = styled.div`
  ${StyledBaseModal} {
    width: 400px;
  }
  ${StyledBaseModalContent} {
    overflow-y: visible;
  }
`;

const PersonLinkEditModal = ({
  info,
  handleChange,
}: PersonLinkEditModalProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const selected = useAppSelector(getSelectedPersonLink);
  const { form, registerField, setFormValue, validate, handleFormChange } =
    useFormHook({
      uuid: undefined,
      type: '',
      link: '',
      linkRaw: '',
      display: '',
      isChanged: false,
      isDeleted: false,
      updatedAt: undefined,
      createdAt: undefined,
    });
  const { openModal, closeModal } = useModalManager();

  useEffect(() => {
    if (!selected.item) return;

    const newForm = {
      ...selected.item,
      type: {
        value: selected.item.type,
        label: t(toTitle(selected.item.type)),
      },
    };

    setFormValue(newForm);
  }, [selected]);

  useEffect(() => {
    if (!info || (info && !info.uuid)) return;
    setFormValue({ uuid: info.uuid, tmpUUID: info.uuid });

    return () => {
      dispatch(setSelectedPersonLinkAction(undefined));
    };
  }, [info]);

  const handleSaveResult = () => {
    if (!validate()) return;

    handleChange &&
      handleChange({
        ...form,
        tmpUUID: form.tmpUUID || uuidv4(),
      });
    closeModal();
  };

  const handleConfirmDelete = (action?: ALERT_ACTION_ENUM) => {
    if (!action || action !== ALERT_ACTION_ENUM.YES) return;
    handleChange &&
      handleChange({
        ...form,
        tmpUUID: form.uuid || form.tmpUUID,
        isDeleted: true,
      });
    closeModal();
  };

  const personLinkTypesLoadOptions = useCallback(async () => {
    const {
      payload: { data },
    } = await dispatch(getPersonLinkTypesThunk(undefined));
    const LINK_TYPE_REPLACEMENTS: Record<string, string> = {
      SKYPE: 'MS_TEAMS',
    };
    const mappedData = data.map(
      (item: string) => LINK_TYPE_REPLACEMENTS[item] || item,
    );
    return {
      options: mappedData.map((item: string) => ({
        value: item,
        label: t(item) !== item ? t(item) : t(toTitle(item)),
      })),
      hasMore: false,
    };
  }, []);

  const renderHeader = useMemo(() => {
    if (!form || (!form.uuid && !form.tmpUUID)) return t('personLink.modalNew');
    return t('personLink.modalEdit');
  }, [form]);

  const renderFooter = useMemo(() => {
    return (
      <StyledPersonLinkEditFooter>
        <StyledPersonLinkEditFooterLeft>
          {(form.uuid || form.tmpUUID) && (
            <IconButtonComponent
              onClick={() =>
                openModal(ExternalLinkDeleteAlertComponent, {
                  handleConfirm: handleConfirmDelete,
                })
              }
            >
              <TrashIcon />
            </IconButtonComponent>
          )}
        </StyledPersonLinkEditFooterLeft>
        <StyledPersonLinkEditFooterRight>
          <ButtonComponent appearance="primary" handleClick={handleSaveResult}>
            {t('Next')}
          </ButtonComponent>
          <ButtonComponent handleClick={() => closeModal()}>
            {t('Cancel')}
          </ButtonComponent>
        </StyledPersonLinkEditFooterRight>
      </StyledPersonLinkEditFooter>
    );
  }, [form]);

  const renderForm = useMemo(
    () => (
      <StyledPersonLinkForm>
        <FormLayoutComponent>
          <StyledPersonLinkFormStack>
            <SelectComponent
              {...registerField('type', {
                validations: [required],
              })}
              value={form.type}
              label={t('Type') + '*'}
              isSearchable={false}
              placeholder={t('Choose type')}
              loadOptions={personLinkTypesLoadOptions}
              onChangeOption={(type: string) => {
                handleFormChange({ type });
              }}
            />

            <InputComponent
              {...registerField('link', {
                validations: [required],
              })}
              value={form.link}
              label={t('External link') + '*'}
              placeholder={t('Enter external link')}
              onChange={(link: string) => {
                handleFormChange({ linkRaw: link, link });
              }}
            />
          </StyledPersonLinkFormStack>
        </FormLayoutComponent>
      </StyledPersonLinkForm>
    ),
    [form],
  );

  return (
    <StyledPersonLinkEdit>
      <BaseModalComponent header={renderHeader} footer={renderFooter}>
        {!info ? renderForm : <SpinnerComponent />}
      </BaseModalComponent>
    </StyledPersonLinkEdit>
  );
};

export default PersonLinkEditModal;
